import produce from 'immer'
import { isNotEmptyElement } from '@feedyou/utils'
import {
  AiTool,
  BotFlow,
  ChannelId,
  DialogId,
  Dictionary,
  IntentId,
  NlpModelId
} from '@feedyou/schema'

export type TreeEntryPoint =
  | `intro`
  | `getStarted`
  | `channels-${ChannelId}-getStarted`
  | `main`
  | `nlp-otherDialog`
  | `nlp-noMatchDialog`
  | `nlp-vulgarismDialog`
  | `nlp-triggers`
  | `nlp-models-${NlpModelId}-defaultDialog`
  | `nlp-models-${NlpModelId}-intents-${IntentId}`
  | `nlp-models-${NlpModelId}-provider-tools-${number}-action-dialog`

export type DialogsPerTreeEntryPoint = Dictionary<TreeEntryPoint, DialogId[]>

export const getUsedDialogsPerTreeEntryPoint = (flow: BotFlow): DialogsPerTreeEntryPoint => {
  return produce({} as DialogsPerTreeEntryPoint, mapDraft => {
    if (flow.intro?.dialog) {
      mapDraft.intro = [flow.intro?.dialog]
    }

    if (flow.getStartedDialogId) {
      mapDraft.getStarted = [flow.getStartedDialogId]
    }

    if (flow?.channels?.length) {
      flow.channels.forEach(channel => {
        const channelId: ChannelId = channel.id || ''
        const getStartedDialogId: string | undefined = channel.getStartedDialogId
        if (channelId && getStartedDialogId) {
          mapDraft[`channels-${channelId}-getStarted`] = [getStartedDialogId]
        }
      })
    }

    mapDraft.main = [flow?.mainDialogId || 'main']

    mapDraft[`nlp-vulgarismDialog`] = [`nlp-vulgarism`]

    const { nlp } = flow

    if (!nlp) {
      return
    }

    if (nlp.otherDialog) {
      mapDraft[`nlp-otherDialog`] = [nlp.otherDialog]
    }

    if (nlp.triggers) {
      const triggerDialogs = nlp.triggers.map(trigger => trigger.dialog).filter(isNotEmptyElement)
      mapDraft[`nlp-triggers`] = triggerDialogs
    }

    if (nlp.noMatchDialog) {
      mapDraft[`nlp-noMatchDialog`] = [nlp.noMatchDialog]
    }

    nlp.models?.forEach(model => {
      if (model.defaultDialog) {
        mapDraft[`nlp-models-${model.id}-defaultDialog`] = [model.defaultDialog]
      }

      model?.provider?.tools?.forEach((tool: AiTool, index: number) => {
        const dialog = tool?.action?.dialog
        if (dialog) {
          mapDraft[`nlp-models-${model.id}-provider-tools-${index}-action-dialog`] = [dialog]
        }
      })

      model?.intents?.forEach(intent => {
        if (intent.dialog) {
          const entryPointId: TreeEntryPoint = `nlp-models-${model.id}-intents-${intent.id}`
          if (!mapDraft[entryPointId]) {
            mapDraft[entryPointId] = []
          }

          mapDraft[entryPointId]!.push(intent.dialog)
        }
      })
    })
  })
}
